// src/components/Feed/PostCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  Typography,
  IconButton,
  Button,
  Box,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Divider,
  Link,
  Grid,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  Share,
  MoreVert,
  Link as LinkIcon,
  PlayArrow,
  ExpandMore,
  ExpandLess,
  Send,
  Reply,
  Flag,
  Edit,
  Delete,
  Bookmark,
  BookmarkBorder,
  Public,
  People,
  Lock,
  Link as LinkIcon2,
  Close,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { toggleLike, createComment, sharePost, deletePost, updatePost, getFileUrl, reportPost } from '../../services/api';
import { bookmarkService } from '../../services/bookmarkService';
import S3Avatar from '../Common/S3Avatar';
import BadgedAvatar from '../Common/BadgedAvatar';
import ConfirmModal from '../Common/ConfirmModal';
import EditPost from './EditPost';
import { achievementService } from '../../services/achievementService';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const PostCard = ({ post, onUpdate, onDelete }) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [liking, setLiking] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reporting, setReporting] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [authorBadges, setAuthorBadges] = useState([]);

  // Load author badges
  useEffect(() => {
    if (post.author?.id) {
      achievementService.getUserBadges(post.author.id)
        .then(setAuthorBadges)
        .catch(() => setAuthorBadges([]));
    }
  }, [post.author?.id]);

  // Check bookmark status
  useEffect(() => {
    if (user?.id && post.id) {
      bookmarkService.isPostBookmarked(post.id, user.id)
        .then(setBookmarked)
        .catch(() => setBookmarked(false));
    }
  }, [user?.id, post.id]);


  const isLiked = Array.isArray(post.likes?.items) && post.likes.items.some(like => like.userID === user?.id);
  const isOwnPost = post.authorID === user?.id;

  useEffect(() => {
    const fetchMediaUrls = async () => {
      const postToUse = post.isReshare && post.originalPost ? post.originalPost : post;
      if (!postToUse.images || postToUse.images.length === 0) {
        setMediaLoading(false);
        return;
      }
      try {
        setMediaLoading(true);
        const authorIdentityId = postToUse.authorIdentityId || postToUse.author?.identityId;
        const isOwnPost = postToUse.authorID === user?.id;
        const isPublicPost = postToUse.visibility === 'PUBLIC';
        
        // Determine access level based on post visibility and ownership
        let accessLevel = 'protected';
        let targetIdentityId = authorIdentityId;
        
        if (isPublicPost) {
          accessLevel = 'public';
          targetIdentityId = null;
        } else if (!isOwnPost && postToUse.visibility === 'PRIVATE') {
          // Private posts - only owner can see media
          setMediaUrls([]);
          setMediaLoading(false);
          return;
        } else if (!authorIdentityId && isOwnPost) {
          // For own posts without authorIdentityId, try without targetIdentityId (current user's files)
          targetIdentityId = null;
        } else if (!authorIdentityId) {
          // For other users' posts without authorIdentityId, can't access protected files
          setMediaUrls([]);
          setMediaLoading(false);
          return;
        }
        
        const urls = await Promise.all(
          postToUse.images.map(key => getFileUrl(key, targetIdentityId, accessLevel))
        );
        setMediaUrls(urls.filter(url => url !== null));
      } catch (error) {
        console.error("Error fetching media URLs for post:", postToUse.id, error);
        setMediaUrls([]);
      } finally {
        setMediaLoading(false);
      }
    };

    fetchMediaUrls();
  }, [post.images, post.visibility, post.authorID, post.originalPost, user?.id, post.author?.identityId]);



  const handleLike = async () => {
    if (liking || !user) return;
    
    setLiking(true);
    try {
      const result = await toggleLike(post.id);
      onUpdate(post.id, result);
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to update like',
      });
    } finally {
      setLiking(false);
    }
  };

  const handleCommentSubmit = async (content, replyToComment = null) => {
    if (!content.trim() || commenting || !user) return;

    setCommenting(true);
    try {
      const comment = await createComment({
        content: content,
        postID: post.id,
        authorID: user.id,
        parentID: replyToComment?.id || null,
      });
      
      const updatedComments = {
        items: [
          ...(post.comments?.items || []),
          {
            ...comment,
            author: { ...user },
          },
        ],
      };
      
      onUpdate(post.id, {
        comments: updatedComments,
        commentsCount: updatedComments.items.length,
      });
      
      setReplyTo(null);
      setShowComments(true);
      
      addNotification({
        type: 'success',
        message: replyToComment ? 'Reply posted!' : 'Comment posted!',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to add comment',
      });
    } finally {
      setCommenting(false);
    }
  };

  const handleShare = async () => {
    try {
      const result = await sharePost(post.id, shareMessage);
      setShareDialogOpen(false);
      setShareMessage('');
      
      // Update original post share count
      onUpdate(post.id, {
        sharesCount: (post.sharesCount || 0) + 1,
      });
      
      addNotification({
        type: 'success',
        message: 'Post reshared successfully! Your followers will see it in their feed.',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to share post',
      });
    }
  };

  const handlePostUpdated = (updatedPost) => {
    onUpdate(post.id, updatedPost);
  };

  const handleDelete = async () => {
    try {
      await deletePost(post.id);
      onDelete?.(post.id);
      addNotification({
        type: 'success',
        message: 'Post deleted successfully',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to delete post',
      });
    }
    setDeleteConfirmOpen(false);
  };

  const handleBookmark = async () => {
    if (bookmarking || !user) return;
    
    setBookmarking(true);
    try {
      await bookmarkService.toggleBookmark(post.id);
      setBookmarked(!bookmarked);
      addNotification({
        type: 'success',
        message: bookmarked ? 'Removed from bookmarks' : 'Added to bookmarks',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to update bookmark',
      });
    } finally {
      setBookmarking(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim() || reporting) return;
    
    setReporting(true);
    try {
      await reportPost({
        postID: post.id,
        reason: reportReason,
        description: reportDescription.trim() || null,
        reportedBy: user.id,
      });
      
      setReportDialogOpen(false);
      setReportReason('');
      setReportDescription('');
      
      addNotification({
        type: 'success',
        message: 'Post reported successfully. Track status in My Reports.',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to report post. Please try again.',
      });
    } finally {
      setReporting(false);
    }
  };

  const reportReasons = [
    'Spam or misleading content',
    'Harassment or bullying',
    'Hate speech or discrimination',
    'Violence or dangerous content',
    'Adult content',
    'Copyright infringement',
    'False information',
    'Other'
  ];

  const renderMedia = () => {
    // Use displayPost for media rendering
    const postToRender = displayPost;
    if (mediaUrls.length > 0 && postToRender.images && postToRender.images.length > 0) {
      return (
        <Box sx={{ 
          mt: { xs: 1.5, sm: 2 }, 
          borderRadius: { xs: 2, sm: 3 }, 
          overflow: 'hidden',
          position: 'relative',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.2)'
            : 'rgba(0, 0, 0, 0.02)',
        }}>
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            navigation={mediaUrls.length > 1}
            pagination={{ 
              clickable: true,
              dynamicBullets: true,
            }}
            style={{
              '--swiper-navigation-color': '#fff',
              '--swiper-pagination-color': '#1976d2',
              '--swiper-navigation-size': window.innerWidth < 600 ? '32px' : '44px',
              borderRadius: window.innerWidth < 600 ? '8px' : '12px',
            }}
            className="post-media-swiper"
          >
            {mediaUrls.map((url, index) => (
              <SwiperSlide key={index}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: '300px', sm: '400px', md: '500px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.1)'
                      : 'rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <img 
                    src={url} 
                    alt={`Post media ${index + 1}`}
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'cover',
                      borderRadius: window.innerWidth < 600 ? '8px' : '12px',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedImageUrl(url);
                      setImageDialogOpen(true);
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box> 
      );
    }
    
    if (postToRender.video) {
      return (
        <Box sx={{ 
          mt: { xs: 1.5, sm: 2 }, 
          position: 'relative',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.2)'
            : 'rgba(0, 0, 0, 0.02)',
        }}>
          <video
            controls
            preload="metadata"
            style={{ 
              width: '100%', 
              borderRadius: window.innerWidth < 600 ? 8 : 12,
              maxHeight: window.innerWidth < 600 ? '300px' : '500px',
              objectFit: 'cover',
              backgroundColor: '#000',
              display: 'block'
            }}
            poster={postToRender.images?.[0]}
          >
            <source src={postToRender.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }
    
    if (mediaLoading && postToRender.images?.length > 0) {
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          sx={{ 
            height: { xs: 150, sm: 200 }, 
            mt: { xs: 1.5, sm: 2 },
            borderRadius: { xs: 2, sm: 3 },
            background: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.1)'
              : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <CircularProgress size={window.innerWidth < 600 ? 32 : 40} />
        </Box>
      );
    }
    
    // Show privacy notice for private posts when user can't access media
    if (postToRender.images?.length > 0 && mediaUrls.length === 0 && !mediaLoading && postToRender.visibility === 'PRIVATE' && postToRender.authorID !== user?.id) {
      return (
        <Box sx={{ 
          mt: { xs: 1.5, sm: 2 }, 
          p: { xs: 1.5, sm: 2 }, 
          bgcolor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255, 152, 0, 0.1)'
            : 'rgba(255, 152, 0, 0.05)',
          border: (theme) => theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 152, 0, 0.2)'
            : '1px solid rgba(255, 152, 0, 0.1)',
          borderRadius: { xs: 2, sm: 3 }, 
          textAlign: 'center',
          backdropFilter: 'blur(10px)',
        }}>
          <Typography 
            variant="body2" 
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <Lock sx={{ fontSize: { xs: 16, sm: 18 } }} />
            Private media - only visible to the author
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const getYouTubeVideoId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getTwitterTweetId = (url) => {
    const regex = /twitter\.com\/\w+\/status\/(\d+)|x\.com\/\w+\/status\/(\d+)/;
    const match = url.match(regex);
    return match ? (match[1] || match[2]) : null;
  };

  const getInstagramPostId = (url) => {
    const regex = /instagram\.com\/p\/([A-Za-z0-9_-]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getSpotifyTrackId = (url) => {
    const regex = /spotify\.com\/track\/([A-Za-z0-9]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getGitHubRepo = (url) => {
    const regex = /github\.com\/([\w.-]+)\/([\w.-]+)/;
    const match = url.match(regex);
    return match ? { owner: match[1], repo: match[2] } : null;
  };

  const getLinkPreviewData = (url) => {
    const hostname = new URL(url).hostname.toLowerCase();
    
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return { type: 'youtube', id: getYouTubeVideoId(url) };
    }
    if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
      return { type: 'twitter', id: getTwitterTweetId(url) };
    }
    if (hostname.includes('instagram.com')) {
      return { type: 'instagram', id: getInstagramPostId(url) };
    }
    if (hostname.includes('spotify.com')) {
      return { type: 'spotify', id: getSpotifyTrackId(url) };
    }
    if (hostname.includes('github.com')) {
      return { type: 'github', data: getGitHubRepo(url) };
    }
    if (hostname.includes('netflix.com')) {
      return { type: 'netflix' };
    }
    if (hostname.includes('imdb.com')) {
      return { type: 'imdb' };
    }
    
    return { type: 'generic' };
  };

  const renderLinks = () => {
    const postToRender = displayPost;
    if (!postToRender.links?.length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {postToRender.links.map((link, index) => {
          try {
            const url = new URL(link);
            const previewData = getLinkPreviewData(link);
            
            // YouTube Preview
            if (previewData.type === 'youtube' && previewData.id) {
              const isPlaying = playingVideo === previewData.id;
              
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, overflow: 'hidden' }}>
                  <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                    {isPlaying ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }}
                      >
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${previewData.id}?autoplay=1&rel=0`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%'
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundImage: `url(https://img.youtube.com/vi/${previewData.id}/maxresdefault.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            '& .play-button': {
                              transform: 'scale(1.1)',
                              backgroundColor: 'rgba(255, 0, 0, 0.9)'
                            }
                          }
                        }}
                        onClick={() => setPlayingVideo(previewData.id)}
                      >
                        <Box
                          className="play-button"
                          sx={{
                            width: 68,
                            height: 48,
                            backgroundColor: 'rgba(255, 0, 0, 0.8)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                        >
                          <PlayArrow sx={{ color: 'white', fontSize: 32, ml: 0.5 }} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          component="img"
                          src="https://www.youtube.com/favicon.ico"
                          alt="YouTube"
                          sx={{ width: 16, height: 16 }}
                        />
                        <Typography variant="body2" color="primary">
                          YouTube Video
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        {isPlaying && (
                          <Button
                            size="small"
                            onClick={() => setPlayingVideo(null)}
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            <Typography variant="caption">Close</Typography>
                          </Button>
                        )}
                        <Button
                          size="small"
                          onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          <Typography variant="caption">Open in YouTube</Typography>
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            }
            
            // Twitter/X Preview
            if (previewData.type === 'twitter' && previewData.id) {
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent 
                    sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#1DA1F2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>𝕏</Typography>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                        Twitter/X Post
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Click to view tweet
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            // Instagram Preview
            if (previewData.type === 'instagram' && previewData.id) {
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent 
                    sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ 
                        width: 20, 
                        height: 20, 
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ color: 'white', fontSize: 10 }}>📷</Typography>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                        Instagram Post
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Click to view on Instagram
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            // Spotify Preview
            if (previewData.type === 'spotify' && previewData.id) {
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent 
                    sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#1DB954', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'white', fontSize: 12 }}>♪</Typography>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                        Spotify Track
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Click to listen on Spotify
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            // GitHub Preview
            if (previewData.type === 'github' && previewData.data) {
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent 
                    sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#333', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'white', fontSize: 12 }}>⚡</Typography>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                        {previewData.data.owner}/{previewData.data.repo}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      GitHub Repository
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            // Netflix Preview
            if (previewData.type === 'netflix') {
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent 
                    sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#E50914', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'white', fontSize: 10, fontWeight: 'bold' }}>N</Typography>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                        Netflix Content
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Watch on Netflix
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            // IMDb Preview
            if (previewData.type === 'imdb') {
              return (
                <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent 
                    sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#F5C518', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'black', fontSize: 10, fontWeight: 'bold' }}>★</Typography>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                        IMDb Page
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      View on IMDb
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            // Regular Link Preview
            return (
              <Card key={index} variant="outlined" sx={{ mb: 1, cursor: 'pointer' }}>
                <CardContent 
                  sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                  onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinkIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
                      {url.hostname}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {url.pathname !== '/' ? url.pathname.slice(0, 20) + '...' : 'Visit site'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          } catch (error) {
            return (
              <Chip
                key={index}
                icon={<LinkIcon />}
                label="Invalid Link"
                onClick={() => window.open(link, '_blank')}
                sx={{ mr: 1, mb: 1 }}
                variant="outlined"
                size="small"
                color="error"
              />
            );
          }
        })}
      </Box>
    );
  };

  const renderHashtags = () => {
    if (!post.hashtags?.length) return null;

    return (
      <Box sx={{ mt: 1 }}>
        {post.hashtags.map((hashtag, index) => (
          <Chip
            key={index}
            label={`#${hashtag}`}
            size="small"
            sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
            color="primary"
            variant="outlined"
            onClick={() => {
              // Navigate to hashtag search
              window.location.href = `/search/%23${hashtag}`;
            }}
          />
        ))}
      </Box>
    );
  };

const ReplyInput = ({ handleComment, commenting, user, replyTo, setReplyTo }) => {
  const [newComment, setNewComment] = useState(''); // Internal state for the input

  const handleSend = () => {
    if (newComment.trim()) {
      handleComment(newComment);
      setNewComment(''); // Clear input after sending
    }
  };

  return (
      <Box>
    {replyTo && (
      <Box
        sx={{
          bgcolor: 'action.hover',
          p: 1,
          borderRadius: 1,
          mb: 1,
          fontSize: '0.875rem',
        }}
      >
        Replying to @{replyTo.author.username && replyTo.author.username.includes('@') ? replyTo.author.username : replyTo.author.displayName}
        <IconButton
          size="small"
          onClick={() => setReplyTo(null)}
          sx={{ ml: 1, p: 0.5 }}
        >
          ×
        </IconButton>
      </Box>
    )}
    <TextField
      fullWidth
      placeholder={replyTo ? "Write a reply..." : "Write a comment..."}
      value={newComment}
      onChange={e => setNewComment(e.target.value)}
      onKeyPress={e => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
         handleSend();
        }
      }}
      disabled={commenting || !user}
      size="small"
      multiline
      maxRows={3}
      sx={{
        bgcolor: 'background.default',
        borderRadius: 1,
        '& .MuiOutlinedInput-root': { borderRadius: 1 }
      }}
      InputProps={{
        endAdornment: (
          <IconButton
            onClick={handleSend}
            disabled={!newComment.trim() || commenting || !user}
            color="primary"
            size="small"
          >
            <Send fontSize="small" />
          </IconButton>
        ),
      }}
    />
  </Box>
);
};

const Comment = ({ comment, isReply = false, onReplyClick, onCommentSubmit, isCommenting, user, replyTo, onCancelReply }) => {
  const [commentAuthorBadges, setCommentAuthorBadges] = useState([]);

  useEffect(() => {
    if (comment.author?.id) {
      achievementService.getUserBadges(comment.author.id)
        .then(setCommentAuthorBadges)
        .catch(() => setCommentAuthorBadges([]));
    }
  }, [comment.author?.id]);

  return (
    <Box sx={{ mb: 2, ml: isReply ? 4 : 0 }}>
      <Box display="flex" gap={1}>
        <BadgedAvatar
          user={comment.author}
          badges={commentAuthorBadges}
          size={28}
        />
        <Box flexGrow={1}>
          <Box
            sx={{
              bgcolor: 'action.hover',
              p: 1.5,
              borderRadius: 2,
              position: 'relative',
            }}
          >
            <Typography variant="caption" fontWeight="medium" color="primary">
              {comment.author?.displayName}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {comment.content}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2} sx={{ mt: 0.5, px: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(comment.createdAt))} ago
            </Typography>
            <Button
              size="small"
              onClick={() => onReplyClick(comment)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <Typography variant="caption">Reply</Typography>
            </Button>
          </Box>
        </Box>
      </Box>
      {replyTo?.id === comment.id && (
        <Box display="flex" gap={1} mt={1} ml={5}>
          <S3Avatar s3Key={user?.avatar} sx={{ width: 28, height: 28 }} />
          <Box flexGrow={1}>
            <ReplyInput
              handleComment={(content) => onCommentSubmit(content, replyTo)}
              commenting={isCommenting}
              user={user}
              replyTo={replyTo}
              setReplyTo={onCancelReply} // Assuming onCancelReply sets replyTo to null
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const renderComments = ({ post, user, handleComment, commenting, replyTo, setReplyTo }) => {
  const comments = post.comments?.items || [];
  const topLevelComments = comments.filter(c => !c.parentID);
  
  // Create a map of replies for each parent comment
  const repliesMap = comments
    .filter(c => c.parentID)
    .reduce((acc, reply) => {
      if (!acc[reply.parentID]) {
        acc[reply.parentID] = [];
      }
      acc[reply.parentID].push(reply);
      return acc;
    }, {});

  const CommentWithReplies = ({ comment, depth = 0 }) => (
    <Box key={comment.id} sx={{ mb: 2 }}>
      <Comment
        comment={comment}
        isReply={depth > 0}
        onReplyClick={setReplyTo}
        onCommentSubmit={handleComment}
        isCommenting={commenting}
        user={user}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
      />
      {/* Render nested replies recursively */}
      {repliesMap[comment.id]?.map(reply => (
        <Box key={reply.id} sx={{ ml: 4, mt: 1 }}>
          <CommentWithReplies comment={reply} depth={depth + 1} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ pb: 2 }}>
      {/* Comment Input */}
      <Box display="flex" gap={1} mb={2}>
        <S3Avatar
          s3Key={user?.avatar}
          sx={{ width: 32, height: 32 }}
        >
          {user?.displayName?.[0]?.toUpperCase()}
        </S3Avatar>
        <Box flexGrow={1}>
          <ReplyInput
            handleComment={handleComment}
            commenting={commenting}
            user={user}
            replyTo={replyTo}
            setReplyTo={setReplyTo}
          />
        </Box>
      </Box>

      {/* Comments List with nested replies */}
      {topLevelComments.map((comment) => (
        <CommentWithReplies key={comment.id} comment={comment} depth={0} />
      ))}

      {comments.length === 0 && (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="text.secondary">
            No comments yet. Be the first to comment!
          </Typography>
        </Box>
      )}
    </Box>
  );
};

  // Check if this is a reshare
  const isReshare = post.isReshare && post.originalPost;
  const displayPost = isReshare ? post.originalPost : post;
  const reshareAuthor = isReshare ? post.author : null;

  return (
    <Box>
      <style>
        {`
          .post-media-swiper .swiper-button-next,
          .post-media-swiper .swiper-button-prev {
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(10px);
            border-radius: 50%;
            width: 44px;
            height: 44px;
            margin-top: -22px;
            transition: all 0.2s ease;
          }
          .post-media-swiper .swiper-button-next:hover,
          .post-media-swiper .swiper-button-prev:hover {
            background: rgba(0, 0, 0, 0.8);
            transform: scale(1.1);
          }
          .post-media-swiper .swiper-pagination-bullet {
            background: rgba(255, 255, 255, 0.5);
            opacity: 1;
            transition: all 0.2s ease;
          }
          .post-media-swiper .swiper-pagination-bullet-active {
            background: #1976d2;
            transform: scale(1.2);
          }
          @media (max-width: 600px) {
            .post-media-swiper .swiper-button-next,
            .post-media-swiper .swiper-button-prev {
              width: 36px;
              height: 36px;
              margin-top: -18px;
            }
          }
        `}
      </style>
      <Card sx={{ 
        mb: { xs: 2, sm: 3 }, 
        mx: 0,
        width: '100%',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: (theme) => theme.palette.mode === 'dark' 
          ? 'linear-gradient(145deg, rgba(18, 18, 18, 0.95) 0%, rgba(30, 30, 30, 0.9) 100%)'
          : 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
        backdropFilter: 'blur(24px) saturate(180%)',
        border: (theme) => theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.04)',
        borderRadius: { xs: 2, sm: 3 },
        boxShadow: (theme) => theme.palette.mode === 'dark'
          ? '0 8px 32px -8px rgba(0, 0, 0, 0.4), 0 2px 8px -2px rgba(0, 0, 0, 0.2)'
          : '0 4px 24px -6px rgba(0, 0, 0, 0.12), 0 2px 8px -2px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.05), transparent)',
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 12px 40px -8px rgba(0, 0, 0, 0.5), 0 4px 16px -4px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px -8px rgba(0, 0, 0, 0.16), 0 4px 16px -4px rgba(0, 0, 0, 0.12)',
        }
      }}>
        {/* Reshare Header */}
        {isReshare && (
          <Box sx={{ 
            px: { xs: 2, sm: 3 }, 
            pt: { xs: 1.5, sm: 2 }, 
            pb: 0,
            borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(0, 0, 0, 0.03)'}`
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Share sx={{ 
                fontSize: 16, 
                color: 'success.main',
                transform: 'rotate(-45deg)'
              }} />
              <Typography 
                variant="body2" 
                color="success.main" 
                sx={{ fontWeight: 500 }}
              >
                <Box 
                  component="span" 
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => window.location.href = `/profile/${reshareAuthor?.username}`}
                >
                  {reshareAuthor?.displayName}
                </Box>
                {' '}reshared
              </Typography>
            </Box>
            {post.reshareMessage && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1, 
                  fontStyle: 'italic',
                  color: 'text.secondary'
                }}
              >
                "{post.reshareMessage}"
              </Typography>
            )}
          </Box>
        )}
        
        <CardHeader
          sx={{ 
            pb: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3 },
            pt: isReshare ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 }
          }}
          avatar={
            <BadgedAvatar 
              user={displayPost.author}
              badges={authorBadges}
              size={window.innerWidth < 600 ? 40 : 48}
              sx={{ 
                cursor: 'pointer',
                border: (theme) => theme.palette.mode === 'dark'
                  ? '2px solid rgba(255, 255, 255, 0.1)'
                  : '2px solid rgba(0, 0, 0, 0.05)',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 6px 16px rgba(0, 0, 0, 0.4)'
                    : '0 4px 12px rgba(0, 0, 0, 0.15)',
                }
              }}
              onClick={() => window.location.href = `/profile/${displayPost.author?.username}`}
            />
          }
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography 
                variant="h6" 
                component={Link}
                href={`/profile/${displayPost.author?.username}`}
                sx={{ 
                  textDecoration: 'none', 
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {displayPost.author?.displayName}
              </Typography>
              {displayPost.author?.verified && (
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 10,
                  }}
                >
                  ✓
                </Box>
              )}
              <Box
                component="span"
                sx={{
                  ml: { xs: 0.5, sm: 1 },
                  px: { xs: 0.75, sm: 1 },
                  py: 0.25,
                  bgcolor: (theme) => {
                    const colors = {
                      PUBLIC: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
                      FOLLOWERS: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)',
                      PRIVATE: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)'
                    };
                    return colors[displayPost.visibility] || colors.FOLLOWERS;
                  },
                  borderRadius: { xs: 1, sm: 1.5 },
                  fontSize: { xs: 9, sm: 10 },
                  fontWeight: 500,
                  color: (theme) => {
                    const colors = {
                      PUBLIC: theme.palette.mode === 'dark' ? '#81c784' : '#4caf50',
                      FOLLOWERS: theme.palette.mode === 'dark' ? '#64b5f6' : '#2196f3',
                      PRIVATE: theme.palette.mode === 'dark' ? '#ffb74d' : '#ff9800'
                    };
                    return colors[displayPost.visibility] || colors.FOLLOWERS;
                  },
                  border: (theme) => {
                    const colors = {
                      PUBLIC: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)',
                      FOLLOWERS: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)',
                      PRIVATE: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'
                    };
                    return `1px solid ${colors[displayPost.visibility] || colors.FOLLOWERS}`;
                  },
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {displayPost.visibility === 'PUBLIC' && <Public sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                {displayPost.visibility === 'FOLLOWERS' && <People sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                {displayPost.visibility === 'PRIVATE' && <Lock sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  {displayPost.visibility === 'PUBLIC' ? 'Public' : displayPost.visibility === 'FOLLOWERS' ? 'Followers' : 'Private'}
                </Typography>
              </Box>
            </Box>
          }
          subheader={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(() => {
                // Get active title from titleOrder or titles array (same logic as UserProfile)
                let activeTitle = null;
                if (displayPost.author?.titleOrder && displayPost.author.titleOrder.length > 0) {
                  activeTitle = displayPost.author.titleOrder[0];
                } else if (displayPost.author?.titles && displayPost.author.titles.length > 0) {
                  activeTitle = displayPost.author.titles[0];
                }
                
                return activeTitle ? (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      fontWeight: 600,
                      color: 'primary.main',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mr: 1,
                      '&:hover::after': {
                        content: '" (Achievement Title)"',
                        fontSize: '0.6rem',
                        fontWeight: 400,
                        color: 'text.secondary',
                        textTransform: 'none',
                      }
                    }}
                  >
                    {activeTitle}
                  </Typography>
                ) : null;
              })()}
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  opacity: 0.8
                }}
              >
                {formatDistanceToNow(new Date(displayPost.createdAt))} ago
              </Typography>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: { xs: 0, sm: 0.5 } }}>
              <IconButton 
                onClick={handleBookmark}
                disabled={bookmarking || !user}
                color={bookmarked ? "primary" : "default"}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    bgcolor: bookmarked ? 'primary.50' : 'action.hover',
                  }
                }}
              >
                {bookmarked ? <Bookmark /> : <BookmarkBorder />}
              </IconButton>
              <IconButton 
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'rotate(90deg)',
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <MoreVert />
              </IconButton>
            </Box>
          }
        />
        
        <CardContent sx={{ 
          pt: 0, 
          px: { xs: 2, sm: 3 },
          pb: { xs: 1, sm: 2 }
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.5,
            }}
          >
            {displayPost.content}
          </Typography>
          {displayPost.hashtags && displayPost.hashtags.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {displayPost.hashtags.map((hashtag, index) => (
                <Chip
                  key={index}
                  label={`#${hashtag}`}
                  size="small"
                  sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    window.location.href = `/search/%23${hashtag}`;
                  }}
                />
              ))}
            </Box>
          )}
          {/* Use displayPost for media and links */}
          {displayPost.images && displayPost.images.length > 0 && renderMedia()}
          {displayPost.links && displayPost.links.length > 0 && renderLinks()}
        </CardContent>

        <CardActions sx={{ 
          justifyContent: 'space-between', 
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 1.5 },
          borderTop: (theme) => theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.05)'
            : '1px solid rgba(0, 0, 0, 0.03)',
        }}>
          <Box display="flex" gap={{ xs: 0.25, sm: 0.5 }}>
            <Button
              startIcon={
                isLiked ? 
                  <Favorite sx={{ color: 'error.main', fontSize: { xs: 18, sm: 20 } }} /> : 
                  <FavoriteBorder sx={{ fontSize: { xs: 18, sm: 20 } }} />
              }
              onClick={handleLike}
              disabled={liking || !user}
              size={window.innerWidth < 600 ? "small" : "medium"}
              sx={{ 
                minWidth: { xs: 'auto', sm: 64 },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                borderRadius: { xs: 1.5, sm: 2 },
                color: isLiked ? 'error.main' : 'text.secondary',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: isLiked 
                    ? (theme) => theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)'
                    : 'action.hover',
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: { xs: 0.25, sm: 0.5 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 500
                }}
              >
                {post.likesCount || 0}
              </Typography>
            </Button>
            
            <Button
              startIcon={<ChatBubbleOutline sx={{ fontSize: { xs: 18, sm: 20 } }} />}
              onClick={() => setShowComments(!showComments)}
              size={window.innerWidth < 600 ? "small" : "medium"}
              sx={{ 
                minWidth: { xs: 'auto', sm: 64 },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                borderRadius: { xs: 1.5, sm: 2 },
                color: 'text.secondary',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'scale(1.05)',
                  color: 'primary.main',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: { xs: 0.25, sm: 0.5 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 500
                }}
              >
                {post.commentsCount || post.comments?.items?.length || 0}
              </Typography>
            </Button>
            
            <Button
              startIcon={<Share sx={{ fontSize: { xs: 18, sm: 20 } }} />}
              onClick={() => setShareDialogOpen(true)}
              disabled={!user}
              size={window.innerWidth < 600 ? "small" : "medium"}
              sx={{ 
                minWidth: { xs: 'auto', sm: 64 },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                borderRadius: { xs: 1.5, sm: 2 },
                color: 'text.secondary',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'scale(1.05)',
                  color: 'success.main',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: { xs: 0.25, sm: 0.5 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 500
                }}
              >
                {post.sharesCount || 0}
              </Typography>
            </Button>
          </Box>
          
          <IconButton 
            onClick={() => setShowComments(!showComments)}
            size={window.innerWidth < 600 ? "small" : "medium"}
            sx={{ 
              transform: showComments ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: { xs: 1.5, sm: 2 },
              '&:hover': {
                bgcolor: 'action.hover',
                transform: showComments ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
              }
            }}
          >
            <ExpandMore sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </CardActions>

        <Collapse in={showComments} timeout="auto" unmountOnExit>
          <Divider sx={{
            borderColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.03)',
          }} />
          <Box sx={{ 
            px: { xs: 2, sm: 3 }, 
            pt: { xs: 1.5, sm: 2 },
            pb: { xs: 1, sm: 1.5 },
            background: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.1)'
              : 'rgba(0, 0, 0, 0.01)',
          }}>
            {renderComments({ post, user, handleComment: handleCommentSubmit, commenting, replyTo, setReplyTo })}
          </Box>
        </Collapse>
      </Card>

      {/* Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {[
          ...(isOwnPost ? [
            <MenuItem key="edit" onClick={() => {
              setAnchorEl(null);
              setEditDialogOpen(true);
            }}>
              <Edit fontSize="small" sx={{ mr: 1 }} />
              Edit Post
            </MenuItem>,
            <MenuItem key="delete" onClick={() => {
              setAnchorEl(null);
              setDeleteConfirmOpen(true);
            }}>
              <Delete fontSize="small" sx={{ mr: 1 }} />
              Delete Post
            </MenuItem>
          ] : [
            <MenuItem key="report" onClick={() => {
              setAnchorEl(null);
              setReportDialogOpen(true);
            }}>
              <Flag fontSize="small" sx={{ mr: 1 }} />
              Report Post
            </MenuItem>
          ]),
          <MenuItem key="copy-link" onClick={() => {
            setAnchorEl(null);
            navigator.share?.({
              title: `Post by ${post.author?.displayName}`,
              text: post.content,
              url: window.location.href,
            }) || navigator.clipboard.writeText(window.location.href);
            addNotification({
              type: 'success',
              message: 'Link copied to clipboard',
            });
          }}>
            <Share fontSize="small" sx={{ mr: 1 }} />
            Copy Link
          </MenuItem>
        ]}
      </Menu>

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.9)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Share sx={{ color: 'success.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Reshare Post
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create a new post in your feed that shares the original content with your followers.
          </Typography>
          
          {/* Original Post Preview */}
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
          }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <BadgedAvatar 
                user={displayPost.author}
                size={24}
              />
              <Typography variant="body2" fontWeight="medium">
                {displayPost.author?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                @{displayPost.author?.username}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {displayPost.content}
            </Typography>
            {displayPost.images && displayPost.images.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                📷 {displayPost.images.length} image{displayPost.images.length > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            placeholder="Add your thoughts about this post... (optional)"
            value={shareMessage}
            onChange={(e) => setShareMessage(e.target.value)}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
              }
            }}
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Your followers will see this reshare in their feed along with the original post.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setShareDialogOpen(false)}
            sx={{
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleShare} 
            variant="contained"
            startIcon={<Share />}
            sx={{
              background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
              }
            }}
          >
            Reshare
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Post Dialog */}
      <EditPost
        post={post}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onPostUpdated={handlePostUpdated}
      />

      {/* Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Report Post</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us understand what's wrong with this post.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Reason for reporting:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            {reportReasons.map((reason) => (
              <Button
                key={reason}
                variant={reportReason === reason ? "contained" : "outlined"}
                size="small"
                onClick={() => setReportReason(reason)}
                sx={{ 
                  mr: 1, 
                  mb: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                {reason}
              </Button>
            ))}
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Additional details (optional)"
            value={reportDescription}
            onChange={(e) => setReportDescription(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReport} 
            variant="contained"
            color="error"
            disabled={!reportReason.trim() || reporting}
          >
            {reporting ? 'Reporting...' : 'Report Post'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            margin: 0,
            maxHeight: '100vh',
            maxWidth: '100vw',
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
        }}
        onClick={() => setImageDialogOpen(false)}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            <Close />
          </IconButton>
          <img
            src={selectedImageUrl}
            alt="Enlarged view"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default PostCard;
